import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { MarkdownContent } from "@/components/ui/MarkdownContent";
import { MarkdownInline } from "@/components/ui/MarkdownText";
import { t } from "@/lib/strings";

function storageKey(storageId: string) {
  return `flow:board-desc-expanded:${storageId}`;
}

interface Props {
  /** Stable per-board key for remembering the expand state. */
  storageId: string;
  description: string;
}

/**
 * Board description shown below the title. Renders NOTHING when empty (zero
 * vertical space — the common case). When set, it costs exactly one clamped
 * line at rest; a chevron expands it to the full formatted text and the choice
 * is remembered per board.
 */
export function BoardDescription({ storageId, description }: Props) {
  const text = description.trim();
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem(storageKey(storageId)) === "1";
    } catch {
      return false;
    }
  });

  if (!text) return null;

  function toggle() {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey(storageId), next ? "1" : "0");
      } catch {
        /* localStorage unavailable — expand state is best-effort */
      }
      return next;
    });
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 4,
        margin: "3px 0 0",
        maxWidth: 720,
      }}
    >
      <button
        type="button"
        onClick={toggle}
        title={expanded ? t.boardDescCollapse : t.boardDescExpand}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          marginTop: 1,
          cursor: "pointer",
          display: "inline-flex",
          flexShrink: 0,
        }}
      >
        <Icon
          name={expanded ? "chevDown" : "chevRight"}
          size={13}
          color="var(--ink-3)"
          stroke={2.2}
        />
      </button>
      <div
        onClick={expanded ? undefined : toggle}
        style={{
          fontSize: 13,
          color: "var(--ink-2)",
          cursor: expanded ? "default" : "pointer",
          minWidth: 0,
        }}
      >
        {expanded ? (
          <MarkdownContent markdown={text} />
        ) : (
          <span
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            <MarkdownInline text={text} />
          </span>
        )}
      </div>
    </div>
  );
}
